import React, { useEffect, useState } from "react";
import api from "../api";
import { formatCurrency } from "../utils/formatCurrency";

function fmt(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

export default function VacationCockpit({ month }) {
  const defaultFrom = month ? `${month}-01` : "";
  const defaultTo   = month
    ? new Date(new Date(`${month}-01`).getFullYear(), new Date(`${month}-01`).getMonth() + 1, 0)
        .toISOString().slice(0, 10)
    : "";

  const [dateFrom,    setDateFrom]    = useState(defaultFrom);
  const [dateTo,      setDateTo]      = useState(defaultTo);
  const [refMonth,    setRefMonth]    = useState(month || "");
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [provision,   setProvision]   = useState(null);  // résultat après création
  const [provLoading, setProvLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (refMonth) params.set("month", refMonth);
      const res = await api.get(`/budget/vacation?${params}`);
      setData(res.data);
    } catch (err) {
      console.error("vacation cockpit error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateProvision = async () => {
    if (!data?.total_surplus) return;
    setProvLoading(true);
    try {
      const res = await api.post("/budget/vacation-provision", {
        annual_target: data.total_surplus,
      });
      setProvision(res.data);
      setShowConfirm(false);
    } catch (err) {
      console.error("vacation provision error:", err);
    } finally {
      setProvLoading(false);
    }
  };

  const inputStyle = {
    padding: "6px 10px", borderRadius: 6,
    border: "1px solid var(--border)", fontSize: 13,
  };

  return (
    <div>
      {/* ── FILTRES ── */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 24 }}>
        <label style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 4 }}>
          Du
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 4 }}>
          Au
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 4 }}>
          Budget de référence (mois)
          <input type="month" value={refMonth} onChange={e => setRefMonth(e.target.value)} style={inputStyle} />
        </label>
        <button
          onClick={() => { setDateFrom(""); setDateTo(""); }}
          disabled={!dateFrom && !dateTo}
          style={{
            padding: "7px 14px", borderRadius: 6, alignSelf: "flex-end",
            border: "1px solid var(--border)", background: "var(--surface)",
            color: (!dateFrom && !dateTo) ? "var(--text-muted)" : "var(--text-soft)",
            fontSize: 13, cursor: (!dateFrom && !dateTo) ? "default" : "pointer",
          }}
        >
          Effacer les dates
        </button>
        <button
          onClick={load}
          style={{
            padding: "7px 18px", borderRadius: 6, border: "none",
            background: "var(--primary)", color: "#fff",
            fontWeight: 600, fontSize: 13, cursor: "pointer",
          }}
        >
          Calculer
        </button>
      </div>

      {loading && <div style={{ color: "var(--text-soft)" }}>Chargement…</div>}

      {data && !loading && (
        <>
          {/* ── KPI ── */}
          <div className="kpi-grid" style={{ marginBottom: 24 }}>
            <div className="kpi-card">
              <div className="kpi-label">Total dépenses vacances</div>
              <div className="kpi-value kpi-danger">{formatCurrency(data.total_vacation)}</div>
            </div>
            <div className="kpi-card" style={{ borderLeft: "4px solid var(--warning)" }}>
              <div className="kpi-label">Total surplus</div>
              <div className="kpi-value" style={{ color: data.total_surplus > 0 ? "var(--warning)" : "var(--success)" }}>
                {data.total_surplus > 0 ? "+" : ""}{formatCurrency(data.total_surplus)}
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Transactions</div>
              <div className="kpi-value">{data.transactions.length}</div>
            </div>
          </div>

          {/* ── OBJECTIF DE PROVISION ── */}
          {data.total_surplus > 0 && !provision && (
            <div style={{
              marginBottom: 28, padding: "16px 20px",
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: 10, display: "flex", alignItems: "center",
              gap: 16, flexWrap: "wrap",
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  Économiser le surplus sur 1 an
                </div>
                <div style={{ fontSize: 13, color: "var(--text-soft)" }}>
                  Objectif annuel : <strong>{formatCurrency(data.total_surplus)}</strong>
                  {" → "}
                  <strong>{formatCurrency(Math.round(data.total_surplus / 12 * 100) / 100)} / mois</strong>
                  {" "}à provisionner dans la catégorie <em>Vacances › Budget vacances</em>.
                </div>
              </div>
              {!showConfirm ? (
                <button
                  onClick={() => setShowConfirm(true)}
                  style={{
                    padding: "8px 18px", borderRadius: 7, border: "none",
                    background: "var(--primary)", color: "#fff",
                    fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  Créer l'objectif de provision
                </button>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={handleCreateProvision}
                    disabled={provLoading}
                    style={{
                      padding: "8px 18px", borderRadius: 7, border: "none",
                      background: "var(--success)", color: "#fff",
                      fontWeight: 600, fontSize: 13, cursor: "pointer",
                    }}
                  >
                    {provLoading ? "En cours…" : "Confirmer"}
                  </button>
                  <button
                    onClick={() => setShowConfirm(false)}
                    style={{
                      padding: "8px 14px", borderRadius: 7, fontSize: 13,
                      border: "1px solid var(--border)", background: "var(--surface)",
                      cursor: "pointer", color: "var(--text-soft)",
                    }}
                  >
                    Annuler
                  </button>
                </div>
              )}
            </div>
          )}

          {provision && (
            <div style={{
              marginBottom: 28, padding: "14px 20px",
              background: "#f0faf4", border: "1px solid var(--success)",
              borderRadius: 10, fontSize: 13,
            }}>
              ✓ Objectif créé : <strong>{formatCurrency(provision.annual_target)}</strong> / an
              → <strong>{formatCurrency(provision.monthly)}</strong> / mois
              dans <em>Vacances › Budget vacances</em>.
              Le Suivi Budget et le tableau Tendances le refléteront dès le prochain chargement.
            </div>
          )}

          {/* ── RÉSUMÉ PAR SOUS-CATÉGORIE ── */}
          <h3 style={{ marginBottom: 12 }}>Détail par sous-catégorie</h3>
          {data.summary.length === 0 ? (
            <p style={{ color: "var(--text-muted)" }}>Aucune transaction vacances sur cette période.</p>
          ) : (
            <div className="table-wrapper" style={{ marginBottom: 32 }}>
              <table>
                <thead>
                  <tr>
                    <th>Catégorie</th>
                    <th>Sous-catégorie</th>
                    <th style={{ textAlign: "right" }}>Budget mensuel réf.</th>
                    <th style={{ textAlign: "right" }}>Dépensé vacances</th>
                    <th style={{ textAlign: "right" }}>Surplus</th>
                  </tr>
                </thead>
                <tbody>
                  {data.summary.map(row => (
                    <tr key={row.subcategory_id ?? "__none__"}
                        style={row.surplus > 0 ? { background: "rgba(255,152,0,0.06)" } : undefined}>
                      <td style={{ color: "var(--text-soft)" }}>{row.category_name}</td>
                      <td style={{ fontWeight: 500 }}>{row.subcategory_name}</td>
                      <td style={{ textAlign: "right" }}>
                        {row.monthly_budget > 0 ? formatCurrency(row.monthly_budget) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>
                        {formatCurrency(row.vacation_spent)}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700,
                                   color: row.surplus > 0 ? "var(--warning)" : "var(--text-muted)" }}>
                        {row.surplus > 0 ? `+${formatCurrency(row.surplus)}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "var(--surface-alt, #f5f5f5)", fontWeight: 700 }}>
                    <td colSpan={3} style={{ textAlign: "right", fontSize: 13 }}>Total surplus</td>
                    <td style={{ textAlign: "right" }}>{formatCurrency(data.total_vacation)}</td>
                    <td style={{ textAlign: "right", color: data.total_surplus > 0 ? "var(--warning)" : "var(--success)" }}>
                      {data.total_surplus > 0 ? "+" : ""}{formatCurrency(data.total_surplus)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* ── LISTE DES TRANSACTIONS ── */}
          <h3 style={{ marginBottom: 12 }}>Transactions vacances</h3>
          {data.transactions.length === 0 ? (
            <p style={{ color: "var(--text-muted)" }}>Aucune transaction vacances sur cette période.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Marchand</th>
                    <th>Sous-catégorie</th>
                    <th style={{ textAlign: "right" }}>Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map(t => (
                    <tr key={t.id}>
                      <td style={{ whiteSpace: "nowrap", color: "var(--text-soft)", fontSize: 13 }}>{fmt(t.date)}</td>
                      <td style={{ fontWeight: 500 }}>{t.merchant || t.raw_label}</td>
                      <td style={{ color: "var(--text-soft)", fontSize: 13 }}>
                        {t.category_name && <span>{t.category_name} · </span>}
                        {t.subcategory_name || <span style={{ color: "var(--text-muted)" }}>Non catégorisé</span>}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 600, color: "var(--danger)" }}>
                        {formatCurrency(Math.abs(t.amount))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
